import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import apiService from '../services/api';
import '../styles/SpinTheWheel.css';
import '../styles/Notifications.css';

const SPIN_DURATION_MS = 4200;
const FULL_TURNS = 7;

const SEGMENTS = [
  { key: 'points-50', title: '50 Points', subtitle: 'Quick boost', type: 'points', value: 50, color: '#f8b400' },
  { key: 'recipe', title: 'Recipe Pick', subtitle: 'New cooking idea', type: 'recipe', value: null, color: '#ff6b6b' },
  { key: 'points-100', title: '100 Points', subtitle: 'Solid reward', type: 'points', value: 100, color: '#00a9a5' },
  { key: 'nothing', title: 'Try Again', subtitle: 'No reward today', type: 'nothing', value: 0, color: '#8b95a7' },
  { key: 'points-200', title: '200 Points', subtitle: 'Nice win', type: 'points', value: 200, color: '#845ec2' },
  { key: 'shield', title: 'Streak Shield', subtitle: 'Protect streak', type: 'shield', value: 1, color: '#0081cf' },
  { key: 'points-500', title: '500 Points', subtitle: 'Big reward', type: 'points', value: 500, color: '#ff8f3f' },
  { key: 'boost', title: 'Double Points', subtitle: '24h multiplier', type: 'boost', value: 'double_points', color: '#e63946' }
];

const normalizeDeg = (value) => {
  const mod = value % 360;
  return mod < 0 ? mod + 360 : mod;
};

const SpinTheWheel = ({ sidebarOpen }) => {
  const navigate = useNavigate();
  const { updateUser } = useUser();

  const [canSpin, setCanSpin] = useState(false);
  const [lastSpinTime, setLastSpinTime] = useState(null);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [wheelRotation, setWheelRotation] = useState(0);

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const spinTimeoutRef = useRef(null);

  const segmentAngle = 360 / SEGMENTS.length;

  const wheelGradient = useMemo(() => {
    const stops = SEGMENTS.map((segment, index) => {
      const start = index * segmentAngle;
      const end = (index + 1) * segmentAngle;
      return `${segment.color} ${start}deg ${end}deg`;
    });

    return `conic-gradient(from -90deg, ${stops.join(', ')})`;
  }, [segmentAngle]);

  useEffect(() => {
    checkSpinAvailability();

    return () => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
      }
    };
  }, []);

  const checkSpinAvailability = async () => {
    try {
      const response = await apiService.checkWheelStatus();
      setCanSpin(Boolean(response.canSpin));
      setLastSpinTime(response.lastSpinTime || null);
    } catch (error) {
      setErrorMessage('Unable to load wheel status. Please try again.');
      setShowErrorMessage(true);
    } finally {
      setLoading(false);
    }
  };

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorMessage(true);
    setTimeout(() => setShowErrorMessage(false), 3500);
  };

  const findSegmentIndexForReward = (reward) => {
    if (!reward || !reward.type) return 0;

    const byTypeAndValue = SEGMENTS.findIndex((segment) => {
      if (segment.type !== reward.type) return false;
      if (segment.type === 'points') return Number(segment.value) === Number(reward.value);
      if (segment.type === 'boost') return String(segment.value) === String(reward.value);
      return true;
    });

    if (byTypeAndValue >= 0) return byTypeAndValue;

    const byType = SEGMENTS.findIndex((segment) => segment.type === reward.type);
    return byType >= 0 ? byType : 0;
  };

  const spinWheel = async () => {
    if (!canSpin) {
      showError('You can spin once per day. Come back for your next spin!');
      return;
    }

    if (spinning) return;

    try {
      setSpinning(true);

      const response = await apiService.spinWheel();
      const reward = response.reward;
      const winningIndex = findSegmentIndexForReward(reward);

      const currentMod = normalizeDeg(wheelRotation);
      const desiredMod = normalizeDeg(-(winningIndex + 0.5) * segmentAngle);
      const alignmentDelta = normalizeDeg(desiredMod - currentMod);

      const jitter = (Math.random() - 0.5) * (segmentAngle * 0.4);
      const nextRotation = wheelRotation + FULL_TURNS * 360 + alignmentDelta + jitter;

      setWheelRotation(nextRotation);

      spinTimeoutRef.current = setTimeout(() => {
        setResult(reward);
        setShowResult(true);
        setCanSpin(false);
        setLastSpinTime(new Date().toISOString());
        setSpinning(false);

        if (reward?.type === 'points') {
          updateUser();
        }
      }, SPIN_DURATION_MS);
    } catch (error) {
      setSpinning(false);
      showError(error?.response?.data?.message || 'Spin failed. Please try again in a moment.');
    }
  };

  const getResultMessage = () => {
    if (!result) return '';

    switch (result.type) {
      case 'points':
        return `You won ${result.value} points!`;
      case 'recipe':
        return `Recipe challenge unlocked!`;
      case 'nothing':
        return 'No reward this time.';
      case 'boost':
        return 'Double points activated!';
      case 'shield':
        return 'Streak shield earned!';
      default:
        return 'You got a reward!';
    }
  };

  const getTimeUntilNextSpin = () => {
    if (!lastSpinTime) return null;

    const now = new Date();
    const nextSpin = new Date(lastSpinTime);
    nextSpin.setDate(nextSpin.getDate() + 1);

    const diff = nextSpin - now;
    if (diff <= 0) return 'Available now';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className={`page-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="loading-container">
          <div className="loading-spinner">Loading wheel...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <div className="wheel-page-content">
        <header className="wheel-hero">
          <p className="wheel-kicker">Daily reward game</p>
          <h1 className="page-title">Spin Wheel</h1>
          <p className="page-subtitle">One spin each day. Hit big points, boosts, and streak protection.</p>
        </header>

        <section className="wheel-layout">
          <div className="wheel-card wheel-main-card">
            <div className="wheel-stage">
              <div className="wheel-pointer" aria-hidden="true"></div>

              <div
                className={`reward-wheel ${spinning ? 'is-spinning' : ''}`}
                style={{
                  background: wheelGradient,
                  transform: `rotate(${wheelRotation}deg)`
                }}
              >
                {SEGMENTS.map((segment, index) => {
                  const angleDeg = index * segmentAngle + segmentAngle / 2 - 90;
                  const angleRad = (angleDeg * Math.PI) / 180;
                  const labelRadius = 37;
                  const x = 50 + Math.cos(angleRad) * labelRadius;
                  const y = 50 + Math.sin(angleRad) * labelRadius;
                  return (
                    <div
                      key={segment.key}
                      className="wheel-label"
                      style={{ left: `${x}%`, top: `${y}%` }}
                    >
                      <span className="wheel-label-title">{segment.title}</span>
                      <span className="wheel-label-subtitle">{segment.subtitle}</span>
                    </div>
                  );
                })}

                <div className="wheel-center">
                  <button
                    type="button"
                    className="spin-trigger"
                    onClick={spinWheel}
                    disabled={!canSpin || spinning}
                  >
                    {spinning ? 'Spinning...' : 'Spin'}
                  </button>
                </div>
              </div>
            </div>

            <div className="spin-status-message">
              {canSpin ? (
                <p className="status-ready">Wheel is ready. Spin now!</p>
              ) : (
                <div className="status-cooldown">
                  <p className="cooldown-label">Next spin in</p>
                  <p className="cooldown-time">{getTimeUntilNextSpin()}</p>
                </div>
              )}
            </div>
          </div>

          <aside className="wheel-card rewards-info-card">
            <h3>Reward Pool</h3>
            <div className="rewards-list">
              {SEGMENTS.map((segment) => (
                <div key={segment.key} className="reward-item">
                  <span className="reward-dot" style={{ background: segment.color }}></span>
                  <div>
                    <p className="reward-label">{segment.title}</p>
                    <p className="reward-sub">{segment.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </section>
      </div>

      {showErrorMessage && (
        <div className="error-notification">
          <div className="error-content">
            <span className="error-icon">!</span>
            <div className="error-text">
              <strong>Oops</strong>
              <span>{errorMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowErrorMessage(false)}>x</button>
          </div>
        </div>
      )}

      {showResult && result && (
        <div className="modal-overlay" onClick={() => setShowResult(false)}>
          <div className="result-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="result-modal-header">
              <h2>{getResultMessage()}</h2>
              <button className="modal-close-btn" onClick={() => setShowResult(false)}>x</button>
            </div>
            <div className="result-modal-body">
              {result.type === 'recipe' && result.recipe && (
                <div className="recipe-result">
                  <img
                    src={result.recipe.imageUrl || 'https://via.placeholder.com/300x200?text=Recipe'}
                    alt={result.recipe.title}
                    className="recipe-result-image"
                  />
                  <h3 className="recipe-result-title">{result.recipe.title}</h3>
                  <p className="recipe-result-desc">{result.recipe.description}</p>
                  <button className="primary-btn" onClick={() => navigate(`/recipes/${result.recipe.id}`)}>
                    View Recipe
                  </button>
                </div>
              )}

              {result.type === 'points' && (
                <div className="points-result">
                  <div className="points-result-value">+{result.value}</div>
                  <div className="points-result-label">Points added to your account</div>
                </div>
              )}

              {result.type === 'boost' && (
                <div className="boost-result">
                  <p className="boost-result-text">Double points boost is active for 24 hours.</p>
                </div>
              )}

              {result.type === 'shield' && (
                <div className="shield-result">
                  <p className="shield-result-text">Your streak is protected for the next 7 days.</p>
                  <p className="shield-result-validity">Use this protection if you miss a day.</p>
                </div>
              )}

              {result.type === 'nothing' && (
                <div className="nothing-result">
                  <p className="nothing-result-text">No reward this round. Try again tomorrow.</p>
                </div>
              )}
            </div>
            <div className="result-modal-footer">
              <button className="secondary-btn" onClick={() => setShowResult(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinTheWheel;
